import type { OrganizationResponse } from '../onboarding/organization-onboarding-contracts'

/**
 * Whether the signed-in user is an Owner or Admin of this clinic, for the
 * technical NexHealth details in Working Hours and Appointments.
 *
 * The role alone is not the authority. `GET /organization` (onboarding-api
 * `organization_dto`) answers only for an ACTIVE OWNER or ADMIN membership of
 * exactly one organization, with explicit access to every clinic it lists, and
 * refuses everyone else (managers, staff, legacy clinic accounts). So this
 * needs that answer, an admin role in it, and this clinic in its list. While
 * the answer is loading or refused, the details stay hidden.
 */
export function canViewClinicTechnicalDetails(
  organization: OrganizationResponse | undefined,
  tenantId: string
) {
  return Boolean(
    organization &&
    (organization.role === 'OWNER' || organization.role === 'ADMIN') &&
    organization.clinics.some((clinic) => clinic.tenantUid === tenantId)
  )
}

/**
 * Whether the signed-in user may open Settings > Organization: an Owner or
 * Admin in the server-verified `GET /organization` answer. Managers, staff and
 * legacy clinic accounts get no answer (403), so the tab, its `?section=` link
 * and the `/organization/*` routes all stay closed for them. Every
 * Organization endpoint still enforces this on the server.
 */
export function canManageOrganization(
  organization: OrganizationResponse | null | undefined
): organization is OrganizationResponse {
  return Boolean(
    organization &&
    (organization.role === 'OWNER' || organization.role === 'ADMIN')
  )
}

/**
 * Whether the signed-in user may manage the Organization Alert Text: only an
 * Organization Administrator (`ADMIN`). Owners also manage the organization,
 * but this setting is the Administrator's alone, and the server refuses an
 * OWNER membership on its own.
 */
export function canManageOrganizationAlert(
  organization: OrganizationResponse | null | undefined
): organization is OrganizationResponse {
  return organization?.role === 'ADMIN'
}
